import PropTypes from "prop-types";
import BaseNode from "./BaseNode";
import { RealisticTransformer } from "./realistic";

/**
 * TransformerNode – Power transformer visualization.
 *
 * Supports:
 * - Realistic 3D SCADA design with high-voltage corona arcs & radiator heat shimmer
 * - Premium glassmorphism design
 * - Classic industrial SVG layout
 */
const TransformerNode = (props) => {
  return (
    <BaseNode {...props}>
      {({ isRunning, isOffline, statusColor, speedScale, ratio, designType, color, opacity, liveValue, unit, width, height }) => {
        if (designType === "realistic") {
          return (
            <RealisticTransformer
              liveValue={liveValue}
              unit={unit || "kW"}
              isRunning={isRunning}
              isOffline={isOffline}
              statusColor={statusColor}
              speedScale={speedScale}
              ratio={ratio}
              opacity={opacity}
              width={width}
              height={height}
            />
          );
        }

        const dotColor =
          statusColor === "green" ? "#22c55e" : statusColor === "red" ? "#ef4444" : "#94a3b8";

        const pulseDuration = isRunning ? `${Math.max(0.4, 1.5 / (speedScale || 1))}s` : "0s";
        const showOldDesign = designType === "new_design" || designType === "classic";

        return (
          <svg
            width="80"
            height="75"
            viewBox="0 0 80 75"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              opacity,
              filter: isOffline ? "grayscale(100%) opacity(0.6)" : "none",
              overflow: "visible",
              transition: "all 0.3s ease",
            }}
          >
            {!showOldDesign ? (
              // ─── NEW PREMIUM GLASSMORPHIC DESIGN ───
              <>
                <defs>
                  {/* Metal tank gradient */}
                  <linearGradient id="xfmrBody" x1="0" y1="0" x2="80" y2="70">
                    <stop offset="0%" stopColor="#475569" />
                    <stop offset="50%" stopColor="#64748b" />
                    <stop offset="100%" stopColor="#334155" />
                  </linearGradient>
                  {/* Bushing gradient */}
                  <linearGradient id="bushingGrad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#7c2d12" />
                    <stop offset="50%" stopColor="#ea580c" />
                    <stop offset="100%" stopColor="#431407" />
                  </linearGradient>
                  <filter id="glowFilter" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="2" result="blur" />
                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                  </filter>
                </defs>

                {/* Conservator tank on top */}
                <rect x="22" y="6" width="36" height="10" rx="3" fill="#334155" stroke="#475569" strokeWidth="1" />
                <rect x="30" y="16" width="4" height="4" fill="#1e293b" />
                <rect x="46" y="16" width="4" height="4" fill="#1e293b" />

                {/* Radiator Fins on Left */}
                <g stroke="#1e293b" strokeWidth="1.5">
                  <line x1="8" y1="26" x2="8" y2="54" />
                  <line x1="12" y1="24" x2="12" y2="56" />
                  <line x1="16" y1="26" x2="16" y2="54" />
                </g>

                {/* Radiator Fins on Right */}
                <g stroke="#1e293b" strokeWidth="1.5">
                  <line x1="64" y1="26" x2="64" y2="54" />
                  <line x1="68" y1="24" x2="68" y2="56" />
                  <line x1="72" y1="26" x2="72" y2="54" />
                </g>

                {/* Main Transformer Tank */}
                <rect x="18" y="20" width="44" height="38" rx="2" fill="url(#xfmrBody)" stroke="#1e293b" strokeWidth="1.5" />

                {/* Temperature/Oil level dial on front */}
                <circle cx="40" cy="38" r="7" fill="#0f172a" stroke="#64748b" strokeWidth="1" />
                <circle cx="40" cy="38" r="5" fill="#f8fafc" />
                {/* Dial needle with active jitter */}
                <line
                  x1="40" y1="38"
                  x2={isRunning ? 44 : 36}
                  y2={isRunning ? 34 : 38}
                  stroke="#ef4444"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                >
                  {isRunning && (
                    <animateTransform
                      attributeName="transform"
                      type="rotate"
                      values="-2 40 38; 2 40 38; -2 40 38"
                      dur="0.15s"
                      repeatCount="indefinite"
                    />
                  )}
                </line>

                {/* High Voltage Bushings (HV) - Left Top */}
                {[23, 29, 35].map((x) => (
                  <g key={x}>
                    {/* Corrugated porcelain look */}
                    <rect x={x - 2.5} y="12" width="5" height="8" rx="1" fill="url(#bushingGrad)" />
                    <line x1={x - 3.5} y1="14" x2={x + 3.5} y2="14" stroke="#431407" strokeWidth="1" />
                    <line x1={x - 3.5} y1="17" x2={x + 3.5} y2="17" stroke="#431407" strokeWidth="1" />
                    {/* Top terminal connector */}
                    <circle cx={x} cy="11" r="1.5" fill="#94a3b8" />
                  </g>
                ))}
                <text x="29" y="10" fontSize="5" fill="#cbd5e1" fontWeight="bold">HV</text>

                {/* Low Voltage Bushings (LV) - Right Top */}
                {[45, 51, 57].map((x) => (
                  <g key={x}>
                    <rect x={x - 1.5} y="14" width="3" height="6" rx="0.5" fill="#64748b" />
                    <circle cx={x} cy="13" r="1" fill="#cbd5e1" />
                  </g>
                ))}
                <text x="51" y="12" fontSize="5" fill="#cbd5e1" fontWeight="bold">LV</text>

                {/* Prominent magnetic flux coupling wave in center when running */}
                {isRunning ? (
                  <g filter="url(#glowFilter)">
                    {/* Concentric electromagnetic wave pulsing outward from main tank */}
                    <rect x="15" y="17" width="50" height="44" rx="3" stroke="#fbbf24" strokeWidth="1.5" fill="none" opacity="0.3">
                      <animate attributeName="stroke-width" values="1;3.5;1" dur="1.5s" repeatCount="indefinite" />
                      <animate attributeName="opacity" values="0.1;0.7;0.1" dur="1.5s" repeatCount="indefinite" />
                    </rect>

                    {/* Electrical sparks between HV bushings */}
                    <path d="M 23,11 Q 26,6 29,11" stroke="#38bdf8" strokeWidth="1.2" fill="none">
                      <animate attributeName="opacity" values="0;0.9;0.1;1;0" dur="0.4s" repeatCount="indefinite" />
                    </path>
                    <path d="M 29,11 Q 32,6 35,11" stroke="#38bdf8" strokeWidth="1.2" fill="none">
                      <animate attributeName="opacity" values="0.8;0;1;0.2;0.7" dur="0.3s" repeatCount="indefinite" />
                    </path>

                    {/* Animated glowing coupling arc/wave */}
                    <path d="M 28,38 A 12,12 0 0,1 52,38" stroke="#fbbf24" strokeWidth="3.5" strokeDasharray="4 4" fill="none">
                      <animate attributeName="stroke-dashoffset" values="0;-16" dur="0.6s" repeatCount="indefinite" />
                    </path>
                    <path d="M 28,38 A 12,12 0 0,0 52,38" stroke="#fbbf24" strokeWidth="3.5" strokeDasharray="4 4" fill="none">
                      <animate attributeName="stroke-dashoffset" values="0;16" dur="0.6s" repeatCount="indefinite" />
                    </path>
                    {/* Glowing core center */}
                    <circle cx="40" cy="38" r="10" fill="#fbbf24" opacity="0.2">
                      <animate attributeName="r" values="8;13;8" dur="1.5s" repeatCount="indefinite" />
                    </circle>
                  </g>
                ) : (
                  // Static core indicator when offline/idle
                  <path d="M 28,38 A 12,12 0 0,1 52,38" stroke="#475569" strokeWidth="1" strokeDasharray="2 2" fill="none" />
                )}

                {/* Status dot on top right */}
                <circle cx="72" cy="10" r="5" fill={dotColor} stroke="#ffffff" strokeWidth="1.5" filter={isRunning ? "url(#glowFilter)" : "none"}>
                  {isRunning && (
                    <animate attributeName="opacity" values="1;0.5;1" dur="1.2s" repeatCount="indefinite" />
                  )}
                </circle>

                {/* Status text */}
                <text x="40" y="70" textAnchor="middle" fontSize="7.5" fontWeight="800" fill={dotColor} style={{ letterSpacing: 0.5 }}>
                  {statusColor === "green" ? "ACTIVE" : statusColor === "red" ? "OFF" : "OFFLINE"}
                </text>
              </>
            ) : (
              // ─── CLASSIC INDUSTRIAL SVG DESIGN ───
              <>
                {/* Primary coil (left) */}
                <circle
                  cx="28" cy="35" r="16"
                  fill={isRunning ? `${color}10` : `${color}05`}
                  stroke={isRunning ? color : "#64748b"}
                  strokeWidth="2"
                />
                <text x="28" y="39" textAnchor="middle" fontSize="10" fontWeight="bold" fill={color}>P</text>

                {/* Secondary coil (right) */}
                <circle
                  cx="52" cy="35" r="16"
                  fill={isRunning ? `${color}10` : `${color}05`}
                  stroke={isRunning ? color : "#64748b"}
                  strokeWidth="2"
                />
                <text x="52" y="39" textAnchor="middle" fontSize="10" fontWeight="bold" fill={color}>S</text>

                {/* Core (center) */}
                <rect x="36" y="25" width="8" height="20" rx="2" fill={`${color}25`} />

                {/* Electrical pulse glow */}
                {isRunning && (
                  <g style={{ animation: `live-electrical-pulse ${pulseDuration} ease-in-out infinite` }}>
                    <circle cx="40" cy="35" r="6" fill={color} opacity="0.2" />
                    <circle cx="40" cy="35" r="3" fill={color} opacity="0.5" />
                  </g>
                )}

                {/* Input/output terminals */}
                <rect x="0" y="31" width="12" height="8" fill={color} rx="2" />
                <rect x="68" y="31" width="12" height="8" fill={color} rx="2" />

                {/* Terminal labels */}
                <text x="5" y="28" fontSize="6" fill={color}>HV</text>
                <text x="70" y="28" fontSize="6" fill={color}>LV</text>

                {/* Status indicator - top right */}
                <circle cx="72" cy="10" r="4" fill={dotColor} />
                <text x="40" y="66" textAnchor="middle" fontSize="7" fontWeight="600" fill={dotColor}>
                  {statusColor === "green" ? "ACTIVE" : statusColor === "red" ? "OFF" : "OFFLINE"}
                </text>
              </>
            )}
          </svg>
        );
      }}
    </BaseNode>
  );
};

TransformerNode.propTypes = { data: PropTypes.object, selected: PropTypes.bool, id: PropTypes.string };
export default TransformerNode;
